import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTimer } from 'react-timer-hook'

import Button from '@/components-latest/primitives/Button'
import Input from '@/components-latest/primitives/Controls/Input'
import Text from '@/components-latest/primitives/Text'
import {
  useSendRestaurantVerificationEmailMutation,
  useVerifyEmailOtpMutation
} from '@/ducks/registration/api/registration.api'

import Styles from './EmailVerificationCard.module.scss'

interface EmailVerificationCardProps {
  email: string
  phone: string
  userName?: string
  onVerificationSuccess: () => void
}

const EmailVerificationCard: React.FC<EmailVerificationCardProps> = ({
  email,
  phone,
  userName,
  onVerificationSuccess
}) => {
  const { t } = useTranslation()
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', ''])
  const [isVerifying, setIsVerifying] = useState(false)
  const [error, setError] = useState<string>('')
  const [isInvalidOtp, setIsInvalidOtp] = useState(false)
  const [isMaxAttemptsReached, setIsMaxAttemptsReached] = useState(false)
  const [cooldownSeconds, setCooldownSeconds] = useState<number | null>(null)
  const [cooldownSecondsResend, setCooldownSecondsResend] = useState<number | null>(null)
  const [isMaxAttemptsReachedResend, setIsMaxAttemptsReachedResend] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const [sendVerificationEmail, { isLoading: isSending }] =
    useSendRestaurantVerificationEmailMutation()
  
  const [verifyOtp] = useVerifyEmailOtpMutation()

  const {
    seconds,
    minutes,
    hours,
    isRunning,
    restart
  } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
    onExpire: () => {
      setIsMaxAttemptsReached(false)
      setCooldownSeconds(null)
      setError('')
    }
  })
  const {
    seconds: resendSeconds,
    minutes: resendMinutes,
    hours: resendHours,
    isRunning: isResendRunning,
    restart: restartResend
  } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
    onExpire: () => {
      setCooldownSecondsResend(null)
      setIsMaxAttemptsReachedResend(false)
    }
  })

  useEffect(() => {
    if (cooldownSeconds !== null && cooldownSeconds > 0) {
      const expiryTime = new Date(Date.now() + cooldownSeconds * 1000)
      restart(expiryTime, true)
    }
  }, [cooldownSeconds, restart])
  useEffect(() => {
    if (cooldownSecondsResend !== null && cooldownSecondsResend > 0) {
      const expiryTime = new Date(Date.now() + cooldownSecondsResend * 1000)
      restartResend(expiryTime, true)
    }
  }, [cooldownSecondsResend, restartResend])

  const handleOtpChange = useCallback(
    (index: number, value: string) => {
      if (value && !/^\d$/.test(value)) return

      const newOtp = [...otp]
      newOtp[index] = value
      setOtp(newOtp)
      setError('')
      setIsInvalidOtp(false)

      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus()
      }
    },
    [otp]
  )

  const handleClick = useCallback(
    (index: number) => {
      const newOtp = [...otp]
      newOtp[index] = ''
      setOtp(newOtp)
      setError('')
    },
    [otp]
  )

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus()
      }
    },
    [otp]
  )

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault()
      const pastedData = e.clipboardData.getData('text').trim()
      const digits = pastedData.replace(/\D/g, '').slice(0, 6).split('')

      if (digits.length === 6) {
        setOtp(digits)
        setError('')
        inputRefs.current[5]?.focus()
      }
    },
    []
  )

  const handleVerify = useCallback(async () => {
    const otpString = otp.join('')
    if (otpString.length !== 6) {
      setError(t('registration.emailVerification.invalidCode'))
      return
    }

    setIsVerifying(true)
    setError('')

    try {
      await verifyOtp({
        email,
        code: otpString
      }).unwrap()
      
      onVerificationSuccess()
    } catch (err: unknown) {
      // RTK Query errors can be in err.data or directly in err
      const error = (err as { data?: any; errorCode?: string; message?: string; retryAfter?: number })?.data || (err as { errorCode?: string; message?: string; retryAfter?: number })
      const errorCode = error?.errorCode
      const errorMessage = error?.message
      const retryAfter = error?.retryAfter
      
      if (errorCode === 'INVALID_OTP' || errorCode === 'INVALID_OTP_FORMAT') {
        setIsInvalidOtp(true)
        setError(t('registration.emailVerification.invalidOtpCode'))
      } else if (errorCode === 'MAX_ATTEMPTS_REACHED') {
        setIsInvalidOtp(false)
        setIsMaxAttemptsReached(true)
        // Clear OTP inputs when max attempts reached
        setOtp(['', '', '', '', '', ''])
        if (retryAfter && retryAfter > 0) {
          // Backend returns retryAfter in seconds (e.g., 52 = 52 seconds)
          setCooldownSeconds(retryAfter)
          // Set initial error message, will be updated by timer
          setError(errorMessage || t('registration.emailVerification.maxAttemptsReached'))
        } else {
          setError(errorMessage || t('registration.emailVerification.maxAttemptsReached'))
        }
      } else if (errorCode === 'COOLDOWN_ACTIVE') {
        if (retryAfter) {
          setCooldownSeconds(retryAfter)
          setIsMaxAttemptsReached(true)
        } else {
          const retryMessage = t('registration.emailVerification.cooldownActiveGeneric')
          setError(retryMessage)
        }
      } else if (errorCode === 'EXPIRED_OTP') {
        setIsInvalidOtp(false)
        setError(t('registration.emailVerification.expiredOtp'))
      } else if (errorCode === 'INVALID_EMAIL') {
        setIsInvalidOtp(false)
        setError(t('registration.emailVerification.invalidEmail'))
      } else {
        setIsInvalidOtp(false)
        setError(
          errorMessage ||
            t('registration.emailVerification.verificationFailed')
        )
      }
    } finally {
      setIsVerifying(false)
    }
  }, [otp, email, verifyOtp, onVerificationSuccess, t])

  const handleResend = useCallback(async () => {
    setError('')
    setIsMaxAttemptsReached(false)
    setCooldownSeconds(null)
    setIsMaxAttemptsReachedResend(false)
    setCooldownSecondsResend(null)
    
    try {
      const response = await sendVerificationEmail({
        email,
        phone,
        userName,
        resend: true
      }).unwrap()
      if (response?.retryAfter && response.retryAfter > 0) {
        setCooldownSecondsResend(response.retryAfter)
        setIsMaxAttemptsReachedResend(true)
        return
      }
      // Keep the OTP values when resending, don't clear them
    } catch (err: unknown) {
      const error = (err as { data?: any; errorCode?: string; message?: string; retryAfter?: number })?.data ||
        (err as { errorCode?: string; message?: string; retryAfter?: number })
      const retryAfter = error?.retryAfter
      if (retryAfter && retryAfter > 0) {
        setCooldownSecondsResend(retryAfter)
        setIsMaxAttemptsReachedResend(true)
        return
      }
      setError(error?.message || t('registration.emailVerification.resendFailed'))
    }
  }, [email, phone, userName, sendVerificationEmail, t])

  const formatTimer = useCallback((hours: number, minutes: number, seconds: number) => {
    const parts: string[] = []
    if (hours > 0) {
      parts.push(String(hours).padStart(2, '0'))
    }
    parts.push(String(minutes).padStart(2, '0'))
    parts.push(String(seconds).padStart(2, '0'))
    return parts.join(':')
  }, [])

  const isOtpComplete = otp.every(digit => digit.trim() !== '')
  const isResendCooldownActive =
    cooldownSecondsResend !== null && (isResendRunning || cooldownSecondsResend > 0)
  const resendSecondsLeft = resendHours * 3600 + resendMinutes * 60 + resendSeconds
  const shouldShowError =
    Boolean(error) || isMaxAttemptsReached || isResendCooldownActive
  const displayError =
    isMaxAttemptsReached && (isRunning || cooldownSeconds !== null)
      ? t('registration.emailVerification.maxAttemptsReachedWithTimer', {
          timer: formatTimer(hours, minutes, seconds)
        })
      : error
        ? error
        : isResendCooldownActive
          ? t('registration.emailVerification.resendMaxAttemptsReachedWithTimer', {
              timer: formatTimer(resendHours, resendMinutes, resendSeconds)
            })
          : ''
  const isVerifyDisabled = !isOtpComplete || isVerifying || isMaxAttemptsReached

  return (
    <div className={Styles.card}>
      <div className={Styles.header}>
        <div className={Styles.titleDescription}>
          <Text variant="nextSubtitle" weight="bold" className={Styles.title}>
            {t('registration.emailVerification.title')}
          </Text>
          <Text variant="nextSubtitle" className={Styles.description}>
            {t('registration.emailVerification.description', { email })}
          </Text>
        </div>
      </div>
      
      <div className={Styles.body}>
        <div className={Styles.inputsResendContainer}>
          <div className={Styles.otpContainer}>
            {otp.map((digit, index) => (
              <Input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onClick={() => handleClick(index)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className={`${Styles.otpInput} ${isInvalidOtp ? Styles.otpInputError : ''}`}
                intent="default"
                autoFocus={index === 0}
                disabled={isMaxAttemptsReached}
              />
            ))}
          </div>

          <div className={Styles.errorContainer}>
            {shouldShowError && (
              <Text 
                variant="caption" 
                className={`${Styles.error} ${isInvalidOtp ? Styles.errorRed : ''}`}>
                {displayError}
              </Text>
            )}
          </div>

          <div className={Styles.resendContainer}>
            <button
              type="button"
              onClick={handleResend}
              disabled={isSending || isResendCooldownActive || isMaxAttemptsReachedResend}
              className={`${Styles.resendButton} ${
                isMaxAttemptsReached ? Styles.resendButtonHidden : ''
              }`}>
              <Text variant="nextSubtitle" className={Styles.resendText}>
                {t('registration.emailVerification.resend')}
              </Text>
            </button>
          </div>
        </div>
      </div>

      <div className={Styles.footer}>
        <Button
          variant="branded"
          size="large"
          onClick={handleVerify}
          disabled={isVerifyDisabled}
          className={`${Styles.verifyButton} ${!isVerifyDisabled ? Styles.verifyButtonEnabled : ''}`}>
          {isVerifying
            ? t('registration.emailVerification.verifying')
            : t('registration.emailVerification.verify')}
        </Button>
      </div>
    </div>
  )
}

export default EmailVerificationCard
